import React from 'react';
import { ChevronDown, DollarSign, Play, Share2, MessageCircle, Facebook, Instagram, Twitter } from 'lucide-react';

/**
 * LoteRow - Individual lot row with responsive design
 * 
 * Desktop: 8-column grid with all data visible
 * Mobile: 4-column compact grid, extra data in expanded section
 */
const LoteRow = ({
    lote,
    isExpanded,
    onToggle,
    onNavigate,
    onShare,
    infoRemate
}) => {
    // Handle device share for mobile
    const handleDeviceShare = async () => {
        const permalink = `https://plazarural.com.uy/remate/${infoRemate.numero}/lote/${lote.numero_lote}`;
        const shareText = `Quiero compartir contigo el lote Nro ${lote.numero_lote} de ${lote.categoria} de ${lote.escritorio}: ${permalink}`;

        if (navigator.share) {
            try {
                await navigator.share({
                    title: `Lote #${lote.numero_lote} - ${lote.categoria}`,
                    text: shareText,
                    url: permalink
                });
            } catch (err) {
                if (err.name !== 'AbortError') {
                    console.error('Share failed:', err);
                }
            }
        } else {
            navigator.clipboard.writeText(shareText);
            alert('Enlace copiado al portapapeles');
        }
    };

    // Get status/price display info
    const getStatusInfo = () => {
        if (lote.venta_estado === 'VE') {
            return { label: 'Vendido', labelMobile: 'Vend.', value: `$${lote.precio_venta}`, bgClass: 'bg-green-50 border-green-200', textClass: 'text-green-700' };
        } else if (lote.venta_estado === 'NV') {
            return { label: 'Estado', labelMobile: '', value: 'No vendido', valueMobile: 'NV', bgClass: 'bg-red-50 border-red-200', textClass: 'text-red-600' };
        } else if (lote.preoferta > 0) {
            const isAccepted = lote.preoferta_estado === 'A';
            return {
                label: 'Preoferta',
                labelMobile: 'Preof.',
                value: `$${lote.preoferta}`,
                bgClass: isAccepted ? 'bg-green-50 border-green-200' : 'bg-yellow-50 border-yellow-200',
                textClass: isAccepted ? 'text-green-700' : 'text-yellow-700'
            };
        }
        return { label: 'Estado', labelMobile: '', value: 'Sin preofertas', valueMobile: 'SP', bgClass: 'bg-gray-50 border-gray-200', textClass: 'text-gray-400' };
    };

    const status = getStatusInfo();

    return (
        <div className="border-l-4 border-green-500 transition-all duration-200 hover:border-green-600 hover:bg-green-50/30">
            {/* Header del lote - Clickable */}
            <button
                onClick={onToggle}
                className="w-full px-3 lg:px-6 py-3 lg:py-4 flex items-center justify-between hover:bg-gradient-to-r hover:from-green-50 hover:to-white transition-all duration-300 ease-in-out text-left group"
            >
                {/* Desktop: 8 columnas | Mobile: 5 columnas (incluye logo escritorio) */}
                <div className="flex-1 grid grid-cols-5 lg:grid-cols-8 gap-1 lg:gap-2 items-center">

                    {/* #Lote - Always visible */}
                    <div className="bg-gradient-to-br from-gray-700 to-gray-800 text-white px-2 py-1 lg:py-1.5 rounded-lg shadow-lg text-center">
                        <span className="text-[8px] lg:text-xs font-semibold block opacity-90">#Lote</span>
                        <p className="text-sm lg:text-lg font-bold">{lote.numero_lote}</p>
                    </div>

                    {/* Cabezas - Always visible */}
                    <div className="bg-gray-50 px-2 py-1 lg:py-1.5 rounded-lg border border-gray-200 text-center">
                        <span className="text-[8px] lg:text-xs text-gray-500 font-semibold block">Cab.</span>
                        <p className="font-bold text-gray-900 text-sm lg:text-base">{lote.cabezas}</p>
                    </div>

                    {/* Peso - Always visible */}
                    <div className="bg-gradient-to-r from-green-50 to-green-100 rounded border border-green-300 text-center py-1 lg:py-2 px-0.5 lg:px-2">
                        <p className="text-[8px] lg:text-[9px] text-green-700 font-semibold">Peso</p>
                        <p className={`font-black text-green-900 ${lote.tiene_peso_terneros && lote.peso_terneros > 0 ? 'text-[10px] lg:text-lg' : 'text-xs lg:text-lg'}`}>
                            {lote.peso_promedio}
                            {lote.tiene_peso_terneros && lote.peso_terneros > 0 && (
                                <span className="text-[9px] lg:text-sm text-green-700">/{lote.peso_terneros}</span>
                            )}
                        </p>
                    </div>

                    {/* Ovinos: Condición Corporal | Vacunos: Clase + Estado - Hidden on mobile */}
                    {lote.tipo_ganado === 'L' ? (
                        <div className="hidden lg:block bg-purple-50 px-2 py-1.5 rounded-lg border border-purple-200 text-center">
                            <span className="text-xs text-purple-600 font-semibold block">C.Corp.</span>
                            <p className="font-bold text-purple-900">{lote.condicion_corporal || 'N/A'}</p>
                        </div>
                    ) : (
                        <>
                            <div className="hidden lg:block bg-gray-50 px-2 py-1.5 rounded-lg border border-gray-200 text-center">
                                <span className="text-xs text-gray-500 font-semibold block">Clase</span>
                                <p className="font-bold text-gray-900 text-sm">{lote.clase || 'N/A'}</p>
                            </div>
                            <div className="hidden lg:block bg-gray-50 px-2 py-1.5 rounded-lg border border-gray-200 text-center">
                                <span className="text-xs text-gray-500 font-semibold block">Estado</span>
                                <p className="font-bold text-gray-900 text-sm">{lote.estado || 'N/A'}</p>
                            </div>
                        </>
                    )}

                    {/* Departamento - Hidden on mobile */}
                    <div className="hidden lg:block bg-gray-50 px-2 py-1.5 rounded-lg border border-gray-200 text-center">
                        <span className="text-xs text-gray-500 font-semibold block">Depto.</span>
                        <p className="font-bold text-gray-900 text-xs truncate">{lote.departamento}</p>
                    </div>

                    {/* Escritorio - Always visible (logo on mobile too) */}
                    <div className="flex bg-gray-50 p-0.5 lg:px-2 lg:py-1.5 rounded-lg border border-gray-200 items-center justify-center">
                        {lote.escritorio_logo ? (
                            <img
                                src={`https://plazarural.com.uy/uploads/escritorios/${lote.escritorio_logo}`}
                                alt={lote.escritorio}
                                className="h-8 lg:h-7 max-w-full object-contain filter brightness-0 invert-[0.2]"
                                title={lote.escritorio}
                            />
                        ) : (
                            <p className="font-bold text-gray-900 text-[8px] lg:text-xs truncate px-1">{lote.escritorio}</p>
                        )}
                    </div>

                    {/* Estado/Precio - Always visible */}
                    <div className={`px-1 lg:px-2 py-1 lg:py-1.5 rounded-lg border text-center ${status.bgClass}`}>
                        {/* Label: mobile vs desktop */}
                        <span className={`text-[8px] lg:text-xs font-semibold block ${status.textClass}`}>
                            <span className="lg:hidden">{status.labelMobile || status.label}</span>
                            <span className="hidden lg:inline">{status.label}</span>
                        </span>
                        {/* Value: mobile vs desktop */}
                        <p className={`font-bold text-xs lg:text-sm ${status.textClass}`}>
                            <span className="lg:hidden">{status.valueMobile || status.value}</span>
                            <span className="hidden lg:inline">{status.value}</span>
                        </p>
                    </div>
                </div>

                <ChevronDown
                    className={`w-4 h-4 lg:w-5 lg:h-5 text-gray-400 ml-2 lg:ml-3 transition-transform duration-300 ease-in-out flex-shrink-0 ${isExpanded ? 'rotate-180' : 'rotate-0'}`}
                />
            </button>

            {/* Detalles expandidos */}
            <div
                style={{
                    maxHeight: isExpanded ? '800px' : '0px',
                    opacity: isExpanded ? 1 : 0,
                    overflow: 'hidden',
                    transition: 'max-height 300ms cubic-bezier(0.4, 0, 0.2, 1), opacity 200ms ease-in-out',
                    transform: 'translateZ(0)',
                    willChange: 'max-height, opacity'
                }}
            >
                <div className="px-3 lg:px-6 py-3 bg-gradient-to-b from-gray-50 to-white">

                    {/* Mobile-only: Data hidden from row */}
                    <div className="lg:hidden grid grid-cols-2 gap-2 mb-3 pb-3 border-b border-gray-200">
                        {lote.tipo_ganado === 'L' ? (
                            <div className="bg-purple-50 px-2 py-1.5 rounded-lg border border-purple-200 text-center">
                                <span className="text-xs text-purple-600 font-semibold block">C. Corporal</span>
                                <p className="font-bold text-purple-900">{lote.condicion_corporal || 'N/A'}</p>
                            </div>
                        ) : (
                            <>
                                <div className="bg-gray-50 px-2 py-1.5 rounded-lg border border-gray-200 text-center">
                                    <span className="text-xs text-gray-500 font-semibold block">Clase</span>
                                    <p className="font-bold text-gray-900 text-sm">{lote.clase || 'N/A'}</p>
                                </div>
                                <div className="bg-gray-50 px-2 py-1.5 rounded-lg border border-gray-200 text-center">
                                    <span className="text-xs text-gray-500 font-semibold block">Estado</span>
                                    <p className="font-bold text-gray-900 text-sm">{lote.estado || 'N/A'}</p>
                                </div>
                            </>
                        )}
                        <div className="bg-gray-50 px-2 py-1.5 rounded-lg border border-gray-200 text-center">
                            <span className="text-xs text-gray-500 font-semibold block">Depto.</span>
                            <p className="font-bold text-gray-900 text-sm truncate">{lote.departamento}</p>
                        </div>
                    </div>

                    {/* Datos adicionales */}
                    <div className="grid grid-cols-2 gap-x-4 lg:gap-x-6 gap-y-1 text-sm text-gray-700">
                        <div className="flex items-center gap-2">
                            <span className="font-semibold text-gray-500 min-w-[60px] lg:min-w-[80px] text-xs lg:text-sm">#Insp:</span>
                            <span className="font-bold text-xs lg:text-sm">{lote.inspeccion_numero}</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <span className="font-semibold text-gray-500 min-w-[60px] lg:min-w-[80px] text-xs lg:text-sm">Vendedor:</span>
                            <span className="font-bold truncate text-xs lg:text-sm">{lote.vendedor}</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <span className="font-semibold text-gray-500 min-w-[60px] lg:min-w-[80px] text-xs lg:text-sm">Horario:</span>
                            <span className="font-bold text-xs lg:text-sm">{lote.horario || 'N/A'}</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <span className="font-semibold text-gray-500 min-w-[60px] lg:min-w-[80px] text-xs lg:text-sm">Raza:</span>
                            <span className="font-bold truncate text-xs lg:text-sm">{lote.raza || 'N/A'}</span>
                        </div>
                    </div>

                    {lote.observaciones && (
                        <div className="mt-3 p-2 bg-yellow-50 rounded border border-yellow-200">
                            <span className="text-xs text-yellow-700 font-semibold">Observaciones:</span>
                            <p className="text-gray-900 text-xs lg:text-sm">{lote.observaciones}</p>
                        </div>
                    )}

                    {/* Botones de acción */}
                    <div className="mt-3 flex gap-2">
                        <button
                            disabled={lote.venta_estado === 'VE' || lote.venta_estado === 'NV'}
                            onClick={() => onNavigate(`/lotes/${lote.id}`)}
                            className={`flex-1 px-3 lg:px-4 py-2 rounded-lg font-semibold text-xs lg:text-sm flex items-center justify-center gap-1 lg:gap-2 transition-all ${lote.venta_estado === 'VE' || lote.venta_estado === 'NV'
                                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                : 'bg-gradient-to-r from-green-600 to-green-500 text-white hover:shadow-lg'
                                }`}
                        >
                            <DollarSign className="w-3 h-3 lg:w-4 lg:h-4" />
                            Preofertar
                        </button>
                        <button
                            onClick={() => onNavigate(`/lotes/${lote.id}`)}
                            className="flex-1 px-3 lg:px-4 py-2 border-2 border-green-600 text-green-600 rounded-lg hover:bg-green-50 transition-all font-semibold text-xs lg:text-sm flex items-center justify-center gap-1 lg:gap-2"
                        >
                            <Play className="w-3 h-3 lg:w-4 lg:h-4" />
                            Ver detalle
                        </button>
                    </div>

                    {/* Compartir */}
                    <div className="mt-3 pt-2 border-t border-gray-100 flex items-center gap-3">
                        <span className="text-xs text-gray-400 font-semibold">Compartir:</span>

                        {/* Mobile: Device share button */}
                        <button
                            onClick={handleDeviceShare}
                            className="lg:hidden flex items-center gap-1 px-3 py-1.5 bg-green-50 text-green-600 rounded-lg hover:bg-green-100 transition-colors text-xs font-semibold"
                        >
                            <Share2 className="w-4 h-4" />
                            Compartir
                        </button>

                        {/* Desktop: Social buttons */}
                        <div className="hidden lg:flex gap-1">
                            <button
                                onClick={() => onShare('whatsapp', lote)}
                                className="p-1.5 rounded bg-green-50 text-green-600 hover:bg-green-100 transition-colors"
                                title="WhatsApp"
                            >
                                <MessageCircle className="w-4 h-4" />
                            </button>
                            <button
                                onClick={() => onShare('facebook', lote)}
                                className="p-1.5 rounded bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors"
                                title="Facebook"
                            >
                                <Facebook className="w-4 h-4" />
                            </button>
                            <button
                                onClick={() => onShare('instagram', lote)}
                                className="p-1.5 rounded bg-pink-50 text-pink-600 hover:bg-pink-100 transition-colors"
                                title="Instagram"
                            >
                                <Instagram className="w-4 h-4" />
                            </button>
                            <button
                                onClick={() => onShare('twitter', lote)}
                                className="p-1.5 rounded bg-gray-100 text-gray-900 hover:bg-gray-200 transition-colors"
                                title="X"
                            >
                                <Twitter className="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default LoteRow;
